import React, { FC, useState, useRef, useEffect } from 'react'
import { HStack } from '@chakra-ui/layout'

import { ftInToInches, HeightUnit, inchesToFtIn } from '@gaia-family/lib'

import { useInputFocus } from '../..'
import { RadioGroup } from '../Radio'
import { InputProps } from '../Input'
import { NumberInput } from '../Number'

export type HeightProps = {
  value: Gaia.Height | null
  onChange: (Height: Gaia.Height) => void
  isInvalid?: boolean
  name?: string
  dataCy?: string
  defaultUnit?: HeightUnit
  setTouched?: (value: boolean, shouldValidate?: boolean | undefined) => void
} & Pick<InputProps, 'variant' | 'onBlur' | 'onFocus'>

const unitOptions = [
  { name: 'cm', value: HeightUnit.Centimetres },
  { name: 'ft/in', value: HeightUnit.Inches },
]

export const HeightInput: FC<HeightProps> = ({
  variant,
  value: height,
  onChange,
  isInvalid,
  onBlur,
  onFocus,
  name,
  dataCy,
  defaultUnit = HeightUnit.Centimetres,
  setTouched,
}) => {
  const mounted = useRef(false)
  const [unit, setUnit] = useState(height?.unit ?? defaultUnit)

  const [primary, setPrimary] = useState<number | null>(
    unit === HeightUnit.Centimetres
      ? height?.value ?? null
      : height?.value
      ? inchesToFtIn(height.value)[0]
      : null
  )
  const [secondary, setSecondary] = useState<number | null>(
    unit === HeightUnit.Centimetres
      ? height?.value ?? null
      : height?.value
      ? inchesToFtIn(height.value)[1]
      : null
  )
  const { handleFocus, handleBlur } = useInputFocus({ onBlur, onFocus })

  const changePrimary = (value: number | null) => setPrimary(value ?? 0)
  const changeSecondary = (value: number | null) => setSecondary(value ?? 0)

  const toggleUnit = (newUnit: HeightUnit) => {
    setUnit(newUnit)
    changePrimary(null)
    changeSecondary(null)
    setTouched?.(false)
  }

  useEffect(() => {
    if (mounted.current) {
      const value =
        unit === HeightUnit.Centimetres
          ? primary
          : ftInToInches(primary ?? 0, secondary ?? 0)
      onChange({
        unit,
        value: Math.round(value ?? 0),
      })
    }
    mounted.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, primary, secondary])

  return (
    <HStack spacing="sm" data-cy={dataCy} alignItems="left" width="100%">
      <HStack spacing="sm" direction="row" flex={1}>
        <NumberInput
          key={unit === HeightUnit.Centimetres ? 'cm' : 'ft'}
          isInvalid={isInvalid}
          placeholder={unit === HeightUnit.Centimetres ? 'cm' : 'ft'}
          onChange={changePrimary}
          value={primary}
          variant={variant}
          onFocus={handleFocus}
          onBlur={handleBlur}
          name={name}
          isNatural={unit === HeightUnit.Inches}
        />

        {unit !== HeightUnit.Centimetres && (
          <NumberInput
            isInvalid={isInvalid}
            placeholder="in"
            onChange={changeSecondary}
            value={secondary}
            variant={variant}
            onFocus={handleFocus}
            onBlur={handleBlur}
            name={name}
            isNatural={false}
          />
        )}
      </HStack>
      <RadioGroup
        options={unitOptions}
        value={unit}
        name="unit"
        onChange={toggleUnit}
        width="fit-content"
      />
    </HStack>
  )
}
